import { Cartesian2, Cartesian3, Matrix3, Ray, Viewer } from "cesium";
import { PositionEvent } from "../wrapper/PositionRayPickerPlugin";
import { MatrixUtils } from "./MatrixUtils";
import { VectorUtils } from "./VectorUtils";

// getNormalQuick
const scratchVecLeft = new Cartesian3();
const scratchVecDown = new Cartesian3();
const scratchNormalLeftDown = new Cartesian3();
const scratchVecRight = new Cartesian3();
const scratchVecUp = new Cartesian3();
const scratchNormalRightUp = new Cartesian3();
const scratchNormalQuickViewDirection = new Cartesian3();

// getNormalFromDepthSamples
const scratchDepthScreenLeft = new Cartesian2();
const scratchDepthScreenRight = new Cartesian2();
const scratchDepthScreenUp = new Cartesian2();
const scratchDepthScreenDown = new Cartesian2();
const scratchDepthWorldLeft = new Cartesian3();
const scratchDepthWorldRight = new Cartesian3();
const scratchDepthWorldUp = new Cartesian3();
const scratchDepthWorldDown = new Cartesian3();
const scratchDepthHorizontal = new Cartesian3();
const scratchDepthVertical = new Cartesian3();
const scratchDepthNormal = new Cartesian3();
const scratchDepthViewDirection = new Cartesian3();

// getNormalDetailed
const scratchNormalDetailedViewDirection = new Cartesian3();
const scratchRay = new Ray();
const scratchUnitaryTransposed = new Matrix3();
export class SurfaceUtils {
  constructor(private readonly _viewer: Viewer) {}

  /**
   * Calculate a cheap hover normal from the already-rendered depth buffer.
   *
   * This intentionally avoids scene.pickFromRay(), which performs offscreen
   * picking and is too expensive for live pointer feedback on large tilesets.
   * Use the detailed/quick ray-pick methods for click-time placement when the
   * extra accuracy is worth the main-thread work.
   */
  getNormalFromDepthSamples(
    ev: Pick<PositionEvent, "position" | "screenPosition">,
    sampleSpreadPixels = 2,
  ): Cartesian3 | null {
    const { screenPosition, position: worldPosition } = ev;
    if (!screenPosition || !worldPosition) return null;

    const screenPosLeft = Cartesian2.fromElements(
      screenPosition.x - sampleSpreadPixels,
      screenPosition.y,
      scratchDepthScreenLeft,
    );
    const screenPosRight = Cartesian2.fromElements(
      screenPosition.x + sampleSpreadPixels,
      screenPosition.y,
      scratchDepthScreenRight,
    );
    const screenPosUp = Cartesian2.fromElements(
      screenPosition.x,
      screenPosition.y - sampleSpreadPixels,
      scratchDepthScreenUp,
    );
    const screenPosDown = Cartesian2.fromElements(
      screenPosition.x,
      screenPosition.y + sampleSpreadPixels,
      scratchDepthScreenDown,
    );

    const worldPositionLeft = this.pickPositionFromDepth(
      screenPosLeft,
      scratchDepthWorldLeft,
    );
    const worldPositionRight = this.pickPositionFromDepth(
      screenPosRight,
      scratchDepthWorldRight,
    );
    const worldPositionUp = this.pickPositionFromDepth(
      screenPosUp,
      scratchDepthWorldUp,
    );
    const worldPositionDown = this.pickPositionFromDepth(
      screenPosDown,
      scratchDepthWorldDown,
    );

    if (
      !worldPositionLeft ||
      !worldPositionRight ||
      !worldPositionUp ||
      !worldPositionDown
    ) {
      return null;
    }

    const horizontal = Cartesian3.subtract(
      worldPositionRight,
      worldPositionLeft,
      scratchDepthHorizontal,
    );
    const vertical = Cartesian3.subtract(
      worldPositionUp,
      worldPositionDown,
      scratchDepthVertical,
    );
    const normal = Cartesian3.cross(horizontal, vertical, scratchDepthNormal);

    if (Cartesian3.dot(normal, normal) === 0) return null;

    const viewDirection = Cartesian3.subtract(
      this._viewer.camera.position,
      worldPosition,
      scratchDepthViewDirection,
    );
    Cartesian3.normalize(viewDirection, viewDirection);
    VectorUtils.orientTowardsDirection(viewDirection, normal);

    return Cartesian3.normalize(normal, new Cartesian3());
  }

  private pickPositionFromDepth(
    screenPosition: Cartesian2,
    result: Cartesian3,
  ): Cartesian3 | null {
    if (!this._viewer.scene.pickPositionSupported) return null;

    try {
      const position = this._viewer.scene.pickPosition(screenPosition);
      return position ? Cartesian3.clone(position, result) : null;
    } catch {
      return null;
    }
  }

  /**
   * Calculate the normal vector of a surface or point cloud using
   * the cross product of two vectors formed by the mouse's screen position and four sample points around it.
   * @param screenPosition - The mouse's screen position.
   * @returns The normal vector.
   */
  getNormalQuick(ev: PositionEvent): Cartesian3 {
    const { screenPosition, position: worldPosition } = ev;
    const objectsToExclude = this._viewer.entities.values.filter(
      (entity) => entity.properties?.excludeFromPicking,
    );
    const sampleSpreadPixels = 2;

    const screenPosLeft = new Cartesian2(
      screenPosition.x,
      screenPosition.y - sampleSpreadPixels,
    );
    const screenPosRight = new Cartesian2(
      screenPosition.x + sampleSpreadPixels,
      screenPosition.y,
    );
    const screenPosUp = new Cartesian2(
      screenPosition.x,
      screenPosition.y + sampleSpreadPixels,
    );
    const screenPosDown = new Cartesian2(
      screenPosition.x - sampleSpreadPixels,
      screenPosition.y,
    );

    const rayLeft = this._viewer.camera.getPickRay(screenPosLeft);
    const rayRight = this._viewer.camera.getPickRay(screenPosRight);
    const rayUp = this._viewer.camera.getPickRay(screenPosUp);
    const rayDown = this._viewer.camera.getPickRay(screenPosDown);

    const worldPositionLeft = ev.object
      ? this._viewer.scene.pickFromRay(rayLeft, objectsToExclude)?.position
      : this._viewer.scene.globe.pick(rayLeft, this._viewer.scene);

    const worldPositionRight = ev.object
      ? this._viewer.scene.pickFromRay(rayRight, objectsToExclude)?.position
      : this._viewer.scene.globe.pick(rayRight, this._viewer.scene);

    const worldPositionUp = ev.object
      ? this._viewer.scene.pickFromRay(rayUp, objectsToExclude)?.position
      : this._viewer.scene.globe.pick(rayUp, this._viewer.scene);

    const worldPositionDown = ev.object
      ? this._viewer.scene.pickFromRay(rayDown, objectsToExclude)?.position
      : this._viewer.scene.globe.pick(rayDown, this._viewer.scene);

    if (
      !worldPositionLeft ||
      !worldPositionRight ||
      !worldPositionUp ||
      !worldPositionDown
    ) {
      // The mouse cursor is above the horizon
      return;
    }

    const vecLeft = Cartesian3.subtract(
      worldPositionLeft,
      worldPosition,
      scratchVecLeft,
    );
    const vecDown = Cartesian3.subtract(
      worldPositionDown,
      worldPosition,
      scratchVecDown,
    );
    const normalLeftDown = Cartesian3.cross(
      vecLeft,
      vecDown,
      scratchNormalLeftDown,
    );

    const vecRight = Cartesian3.subtract(
      worldPositionRight,
      worldPosition,
      scratchVecRight,
    );
    const vecUp = Cartesian3.subtract(
      worldPositionUp,
      worldPosition,
      scratchVecUp,
    );
    const normalRightUp = Cartesian3.cross(
      vecRight,
      vecUp,
      scratchNormalRightUp,
    );

    const viewDirection = Cartesian3.subtract(
      this._viewer.camera.position,
      worldPosition,
      scratchNormalQuickViewDirection,
    );

    Cartesian3.normalize(viewDirection, viewDirection);

    // Ensure normals point towards camera
    VectorUtils.orientTowardsDirection(viewDirection, normalLeftDown);
    VectorUtils.orientTowardsDirection(viewDirection, normalRightUp);

    const combinedNormal = Cartesian3.add(
      normalLeftDown,
      normalRightUp,
      scratchNormalQuickViewDirection,
    );
    return Cartesian3.normalize(combinedNormal, new Cartesian3());
  }

  /**
   * Calculate the normal vector of a surface or point cloud using
   * Principal Component Analysis (https://en.wikipedia.org/wiki/Principal_component_analysis)
   * @param screenPosition - The mouse's screen position.
   * @returns The normal vector.
   */
  getNormalDetailed(ev: PositionEvent): Cartesian3 {
    const normal = this.calculateDetailedNormal(
      ev,
      this.getDetailedSamplePoints(ev),
    );
    return normal ?? undefined;
  }

  async getNormalDetailedAsync(
    ev: PositionEvent,
    abortSignal?: AbortSignal,
  ): Promise<Cartesian3 | null> {
    const samplePoints = await this.getDetailedSamplePointsAsync(
      ev,
      abortSignal,
    );
    if (abortSignal?.aborted) return null;

    return this.calculateDetailedNormal(ev, samplePoints);
  }

  private getDetailedSamplePoints(ev: PositionEvent): Cartesian3[] {
    const { screenPosition } = ev;

    // Create sample points in a circle around the mouse position and cast rays to each.
    const sampleSpreadPixels = 2;
    const sampleSize = 32;

    const samplePoints: Cartesian3[] = [];
    const objectsToExclude = this._viewer.entities.values.filter(
      (entity) => entity.properties?.excludeFromPicking,
    );

    for (let i = 0; i < sampleSize; i++) {
      const angle = (i / sampleSize) * Math.PI * 2;
      const offsetX = Math.cos(angle) * sampleSpreadPixels;
      const offsetY = Math.sin(angle) * sampleSpreadPixels;

      const sampleScreenPos = new Cartesian2(
        screenPosition.x + offsetX,
        screenPosition.y + offsetY,
      );

      const ray = this._viewer.camera.getPickRay(sampleScreenPos, scratchRay);
      if (!ray) continue;

      const pickedPosition = ev.object
        ? this._viewer.scene.pickFromRay(ray, objectsToExclude, 0.005)?.position
        : this._viewer.scene.globe.pick(ray, this._viewer.scene);

      if (pickedPosition) samplePoints.push(pickedPosition);
    }

    return samplePoints;
  }

  private async getDetailedSamplePointsAsync(
    ev: PositionEvent,
    abortSignal?: AbortSignal,
  ): Promise<Cartesian3[]> {
    const { screenPosition } = ev;

    // Create sample points in a circle around the mouse position and cast rays to each.
    const sampleSpreadPixels = 2;
    const sampleSize = 32;

    const samplePoints: Cartesian3[] = [];
    const objectsToExclude = this._viewer.entities.values.filter(
      (entity) => entity.properties?.excludeFromPicking,
    );

    for (let i = 0; i < sampleSize; i++) {
      if (abortSignal?.aborted) return samplePoints;

      const angle = (i / sampleSize) * Math.PI * 2;
      const offsetX = Math.cos(angle) * sampleSpreadPixels;
      const offsetY = Math.sin(angle) * sampleSpreadPixels;

      const sampleScreenPos = new Cartesian2(
        screenPosition.x + offsetX,
        screenPosition.y + offsetY,
      );

      const ray = this._viewer.camera.getPickRay(sampleScreenPos);
      if (!ray) {
        await this.yieldToMainThread();
        continue;
      }

      const pickedPosition = ev.object
        ? this._viewer.scene.pickFromRay(ray, objectsToExclude, 0.005)?.position
        : this._viewer.scene.globe.pick(ray, this._viewer.scene);

      if (pickedPosition) samplePoints.push(Cartesian3.clone(pickedPosition));
      await this.yieldToMainThread();
    }

    return samplePoints;
  }

  private async yieldToMainThread() {
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }

  private calculateDetailedNormal(
    ev: PositionEvent,
    samplePoints: Cartesian3[],
  ): Cartesian3 | null {
    const { position: worldPosition } = ev;

    if (samplePoints.length < 3) {
      console.warn("Not enough sample points found for PCA");
      return null;
    }

    const covarianceMatrix = MatrixUtils.getCovarianceMatrix(samplePoints);
    const eigenDecomposition =
      Matrix3.computeEigenDecomposition(covarianceMatrix);

    const eigenvalues = [
      Math.abs(eigenDecomposition.diagonal[Matrix3.getElementIndex(0, 0)]),
      Math.abs(eigenDecomposition.diagonal[Matrix3.getElementIndex(1, 1)]),
      Math.abs(eigenDecomposition.diagonal[Matrix3.getElementIndex(2, 2)]),
    ];

    /**
     * Reference: https://github.com/CesiumGS/cesium/blob/b44fddb17cc0ffb249abeac8d21c06a788d0d64c/packages/engine/Source/Core/Matrix3.js#L1424
     * matrix = unitary matrix * diagonal matrix * transpose(unitary matrix)
     */
    const unitaryTransposed = Matrix3.transpose(
      eigenDecomposition.unitary,
      scratchUnitaryTransposed,
    );

    // Extract the eigenvector corresponding to the smallest eigenvalue
    const minEigenvalueIndex = eigenvalues.indexOf(Math.min(...eigenvalues));
    const normal = new Cartesian3(
      unitaryTransposed[Matrix3.getElementIndex(0, minEigenvalueIndex)],
      unitaryTransposed[Matrix3.getElementIndex(1, minEigenvalueIndex)],
      unitaryTransposed[Matrix3.getElementIndex(2, minEigenvalueIndex)],
    );

    const viewDirection = Cartesian3.subtract(
      this._viewer.camera.position,
      worldPosition,
      scratchNormalDetailedViewDirection,
    );

    Cartesian3.normalize(viewDirection, viewDirection);
    // Ensure normal points towards camera
    VectorUtils.orientTowardsDirection(viewDirection, normal);
    return Cartesian3.normalize(normal, new Cartesian3());
  }
}
